// use-fiscal — React Query hooks pra Epic 10.
// Manual emit / cancel / list / detail / DLQ.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  cancelFiscalInvoice,
  checkCanCancelFiscalInvoice,
  discardFiscalInvoice,
  downloadFiscalPdf,
  downloadFiscalXml,
  emitManualFiscalInvoice,
  emitStandaloneFiscalInvoice,
  getFiscalInvoice,
  listFiscalInvoices,
  reprocessFiscalInvoice,
  searchNbsCodes,
  NBS_MIN_SEARCH,
  type FiscalInvoiceFilters,
  type ManualEmitPayload,
  type StandaloneEmitPayload,
} from '../lib/fiscal-api';

export const fiscalKeys = {
  all: ['fiscal'] as const,
  lists: () => [...fiscalKeys.all, 'list'] as const,
  list: (filters: FiscalInvoiceFilters) => [...fiscalKeys.lists(), filters] as const,
  details: () => [...fiscalKeys.all, 'detail'] as const,
  detail: (id: string) => [...fiscalKeys.details(), id] as const,
  byCharge: (chargeId: string) => [...fiscalKeys.all, 'by-charge', chargeId] as const,
  canCancel: (id: string) => [...fiscalKeys.all, 'can-cancel', id] as const,
  nbsCodes: (search: string) => [...fiscalKeys.all, 'nbs-codes', search] as const,
};

/**
 * Busca de NBS. A lista oficial é estática (930 códigos) — cache de 1h e só
 * 1 nova tentativa: se o Asaas caiu, a tela avisa rápido e deixa emitir sem NBS.
 */
export function useNbsCodes(search: string) {
  return useQuery({
    queryKey: fiscalKeys.nbsCodes(search),
    queryFn: ({ signal }) => searchNbsCodes(search, signal),
    enabled: search.length >= NBS_MIN_SEARCH,
    staleTime: 60 * 60_000,
    retry: 1,
  });
}

export function useFiscalInvoices(filters: FiscalInvoiceFilters = {}) {
  return useQuery({
    queryKey: fiscalKeys.list(filters),
    queryFn: ({ signal }) => listFiscalInvoices(filters, signal),
    staleTime: 15_000,
  });
}

export function useFiscalInvoice(id: string | undefined) {
  return useQuery({
    queryKey: id ? fiscalKeys.detail(id) : ['fiscal', 'detail', 'noop'],
    queryFn: ({ signal }) => getFiscalInvoice(id as string, signal),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

/**
 * Busca NF ativa associada a uma charge. Útil pra mostrar/esconder
 * botões de "Emitir NF" e "Cancelar NF" no detalhe da charge.
 * Retorna undefined se a charge não tem NF.
 */
export function useFiscalInvoiceByCharge(chargeId: string | undefined) {
  return useQuery({
    queryKey: chargeId ? fiscalKeys.byCharge(chargeId) : ['fiscal', 'by-charge', 'noop'],
    queryFn: async ({ signal }) => {
      const result = await listFiscalInvoices({ charge_id: chargeId, pageSize: 1 }, signal);
      return result.data[0] ?? null;
    },
    enabled: Boolean(chargeId),
    staleTime: 15_000,
  });
}

export function useCanCancelFiscalInvoice(id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: id ? fiscalKeys.canCancel(id) : ['fiscal', 'can-cancel', 'noop'],
    queryFn: () => checkCanCancelFiscalInvoice(id as string),
    enabled: Boolean(id) && enabled,
    staleTime: 60_000,
  });
}

// ============================================================================
// Mutations
// ============================================================================

export function useEmitManualFiscalInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ManualEmitPayload) => emitManualFiscalInvoice(payload),
    onSuccess: (_, payload) => {
      qc.invalidateQueries({ queryKey: fiscalKeys.lists() });
      qc.invalidateQueries({ queryKey: fiscalKeys.byCharge(payload.charge_id) });
    },
  });
}

export function useEmitStandaloneFiscalInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: StandaloneEmitPayload) => emitStandaloneFiscalInvoice(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: fiscalKeys.lists() });
    },
  });
}

export function useCancelFiscalInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelFiscalInvoice(id, reason),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: fiscalKeys.detail(id) });
      qc.invalidateQueries({ queryKey: fiscalKeys.lists() });
    },
  });
}

export function useReprocessFiscalInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => reprocessFiscalInvoice(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: fiscalKeys.detail(id) });
      qc.invalidateQueries({ queryKey: fiscalKeys.lists() });
    },
  });
}

export function useDiscardFiscalInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      discardFiscalInvoice(id, reason),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: fiscalKeys.detail(id) });
      qc.invalidateQueries({ queryKey: fiscalKeys.lists() });
    },
  });
}

// ============================================================================
// Downloads (não usa cache — direct download)
// ============================================================================

export async function triggerFiscalDownload(
  id: string,
  format: 'pdf' | 'xml',
  filename: string,
): Promise<void> {
  const blob = format === 'pdf' ? await downloadFiscalPdf(id) : await downloadFiscalXml(id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
